import { useCallback, useEffect, useRef, useState } from "react";

const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
const CONSTRAINTS = { audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 } };

// Start / Finish Speaking: records until the customer taps Finish (pauses never end it), shows live words from the
// browser's own speech recogniser (Chrome → Google, Safari → Apple) and returns the audio for the final transcript.
export function useLiveSpeech({ maxSeconds = 90 } = {}) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [finalText, setFinalText] = useState("");
  const [interim, setInterim] = useState("");
  const [liveOk, setLiveOk] = useState(!!SR);
  const r = useRef({});

  const cleanup = useCallback(() => {
    const c = r.current;
    clearInterval(c.timer);
    c.active = false;
    try { c.rec?.abort(); } catch { /* already stopped */ }
    c.stream?.getTracks().forEach((t) => t.stop());
    c.stream = null; c.rec = null;
    setRecording(false); setInterim("");
  }, []);

  const startRecognition = useCallback(() => {
    if (!SR) return;
    const c = r.current;
    const rec = new SR();
    rec.lang = "en-AU"; rec.continuous = true; rec.interimResults = true; rec.maxAlternatives = 1;
    let committed = 0; // results of THIS recogniser already added to the final text — never appended twice
    rec.onresult = (e) => {
      let add = ""; let tmp = "";
      for (let i = committed; i < e.results.length; i += 1) {
        const res = e.results[i];
        if (res.isFinal && i === committed) { add += res[0].transcript; committed += 1; } else tmp += res[0].transcript;
      }
      if (add) setFinalText((f) => `${f} ${add}`.replace(/\s+/g, " ").trim());
      setInterim(tmp.trim());
    };
    rec.onerror = (e) => { if (["not-allowed", "service-not-allowed", "audio-capture"].includes(e.error)) { setLiveOk(false); c.noLive = true; } };
    rec.onend = () => { setInterim(""); if (c.active && !c.noLive) { try { startRecognition(); } catch { /* ignore */ } } };
    c.rec = rec;
    try { rec.start(); } catch { setLiveOk(false); }
  }, []);

  const start = useCallback(async ({ keepText = false } = {}) => {
    const c = r.current;
    if (c.active) return;
    const stream = await navigator.mediaDevices.getUserMedia(CONSTRAINTS);
    const type = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";
    const mr = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
    c.chunks = []; c.stream = stream; c.mr = mr; c.type = type || "audio/webm"; c.active = true; c.noLive = false;
    mr.ondataavailable = (e) => e.data.size && c.chunks.push(e.data);
    mr.start(1000);
    if (!keepText) setFinalText("");
    setSeconds(0); setRecording(true);
    const t0 = Date.now();
    c.timer = setInterval(() => {
      const s = Math.floor((Date.now() - t0) / 1000);
      setSeconds(s);
      if (s >= maxSeconds) c.onLimit?.();
    }, 250);
    startRecognition();
  }, [maxSeconds, startRecognition]);

  // Resolves with { blob, ext } of exactly this recording; the buffer is then discarded.
  const finish = useCallback(() => new Promise((resolve) => {
    const c = r.current;
    if (!c.mr || c.mr.state === "inactive") { cleanup(); resolve(null); return; }
    c.mr.onstop = () => {
      const blob = new Blob(c.chunks, { type: c.type });
      c.chunks = [];
      resolve(blob.size ? { blob, ext: c.type.includes("mp4") ? "mp4" : "webm" } : null);
    };
    try { c.rec?.stop(); } catch { /* ignore */ }
    c.mr.stop();
    cleanup();
  }), [cleanup]);

  const cancel = useCallback(() => {
    const c = r.current;
    if (c.mr && c.mr.state !== "inactive") { c.mr.onstop = null; c.mr.stop(); }
    c.chunks = [];
    cleanup();
  }, [cleanup]);

  useEffect(() => () => cancel(), [cancel]);
  const onLimit = useCallback((fn) => { r.current.onLimit = fn; }, []);
  return { recording, seconds, finalText, interim, liveOk, setFinalText, start, finish, cancel, onLimit, supported: !!navigator.mediaDevices?.getUserMedia };
}
